import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { Row, Col, Label } from 'reactstrap';

// Models
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleVinModel, buildStylesBasicPathFromVin } from 'client/data/models/vehicle-vin';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';

// Constants
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';

// Context
import {
  useAppraisalTabsConfig,
  useAppraisalTabsState,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

// Hooks
import { useVinFormState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-vin-form-state';
import { useVinDecoder } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-vin-decoder';
import { useStyleSelection } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-style-selection';
import { useVinSubmit } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/hooks/use-vin-submit';

// Components
import { AppraiseTabContentFormWrapper } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraise-tab-content-form-wrapper';
import { AppraisalTabsSubmitButton } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tabs-submit-button';
import { PrivatePartyButton } from 'site-modules/shared/components/appraisal/private-party-button/private-party-button';
import { VinInput } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/vin-tab/vin-input';
import { FindVinLink } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/tabs/vin-tab/find-vin-link';
import {
  appraisalTabViewPropType,
  getResolvedAppraisalTabViewProps,
} from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/shared/appraisal-tab-view';

export function VinTabUI({
  vin = '',
  queryVin = '',
  isVinEdited = false,
  setIsVinEdited = noop,
  setStylesAndVin = noop,
  ctaText = 'Appraise My Car',
  profileOfferVin = '',
  hasAddressToVinDrawer = false,
  isLookUpStyleView = false,
  isBackgroundStyleTabEnabled = false,
  isVdpEmbedded = false,
  isProjectEveStyle = false,
  onFindVinModalOpen = noop,
  onFindVinModalClose = noop,
  vinDecoderStyles = null,
  isAppraiserPageOpened = false,
  wasVinPrePopulated = false,
  setWasVinPrePopulated = noop,
  isActiveTab = false,
  isPrivatePartyBtn = false,
  isWIDG1125chal1 = false,
  isSellInCityState = false,
  creativeId = APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID,
  storage = null,
  apiMetrics,
  setModelValue = noop,
  vinStyles = null,
  view = null,
  isFullWidth = false,
}) {
  const vinInputId = useId();

  const resolvedView = getResolvedAppraisalTabViewProps({
    view,
    profileOfferVin,
    hasAddressToVinDrawer,
    isLookUpStyleView,
    isBackgroundStyleTabEnabled,
    isVdpEmbedded,
    isPrivatePartyBtn,
    isWIDG1125chal1,
    isSellInCityState,
    isFullWidth,
  });

  const {
    vinInputValue,
    isVinValid,
    isVinValidLength,
    wasFormSubmitted,
    isSubmitting,
    setWasFormSubmitted,
    setIsSubmitting,
    onVinChange,
  } = useVinFormState({
    vin,
    queryVin,
    profileOfferVin: resolvedView.profileOfferVin,
    isLookUpStyleView: resolvedView.isLookUpStyleView,
    isVinEdited,
    setIsVinEdited,
    wasVinPrePopulated,
    setWasVinPrePopulated,
    storage,
  });

  const { decodeVin } = useVinDecoder({
    creativeId,
    apiMetrics,
    setModelValue,
  });

  const { onSubmit, resetIsSubmitting } = useVinSubmit({
    creativeId,
    isVinValid,
    vinInputValue,
    setWasFormSubmitted,
    setIsSubmitting,
    decodeVin,
    setStylesAndVin,
    isWIDG1125chal1: resolvedView.isWIDG1125chal1,
    isSellInCityState: resolvedView.isSellInCityState,
    isPrivatePartyBtn: resolvedView.isPrivatePartyBtn,
    isAppraiserPageOpened,
  });

  const { isDecodedVinInvalid } = useStyleSelection({
    vinStyles,
    vinDecoderStyles,
    wasFormSubmitted,
    isVinValid,
    isSubmitting,
    resetIsSubmitting,
  });

  const isError = wasFormSubmitted && (!isVinValid || isDecodedVinInvalid);
  const btnColLgSize = resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle ? 4 : 12;
  const vinInputColLgSize = resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle ? 6 : 12;

  return (
    <div className="appraise-by-vin-tab">
      <Label className="size-12" for={vinInputId}>
        Vehicle Identification Number (VIN)
      </Label>

      <Row>
        <Col xs={12} lg={vinInputColLgSize}>
          <AppraiseTabContentFormWrapper isFullWidth={resolvedView.isFullWidth}>
            <Row>
              <Col xs={12}>
                <VinInput
                  className={classnames({
                    'mb-lg-0': resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle,
                  })}
                  vinInputValue={vinInputValue}
                  onVinChange={onVinChange}
                  isError={isError}
                  isVinValid={isVinValid}
                  isVinValidLength={isVinValidLength}
                  isDecodedVinInvalid={isDecodedVinInvalid}
                  inputId={vinInputId}
                />
              </Col>
            </Row>
          </AppraiseTabContentFormWrapper>
        </Col>

        <Col xs={12} lg={btnColLgSize}>
          <FindVinLink
            profileOfferVin={resolvedView.profileOfferVin}
            isLookUpStyleView={resolvedView.isLookUpStyleView}
            hasAddressToVinDrawer={resolvedView.hasAddressToVinDrawer}
            isBackgroundStyleTabEnabled={resolvedView.isBackgroundStyleTabEnabled}
            isProjectEveStyle={isProjectEveStyle}
            onFindVinModalOpen={onFindVinModalOpen}
            onFindVinModalClose={onFindVinModalClose}
          />

          {resolvedView.isPrivatePartyBtn ? (
            <PrivatePartyButton
              onClick={onSubmit}
              data-test="appraise-btn-by-vin"
              isDisabled={isDecodedVinInvalid || isSubmitting}
              isSubmitting={isSubmitting}
              isActiveTab={isActiveTab}
            />
          ) : (
            <AppraisalTabsSubmitButton
              ctaText={ctaText}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
              containerClassName={classnames({
                'mt-0_5': resolvedView.isLookUpStyleView,
              })}
              buttonClassName={classnames('btn-responsive-md-down', {
                'py-0_5': resolvedView.isVdpEmbedded || resolvedView.isBackgroundStyleTabEnabled || isProjectEveStyle,
              })}
              isDisabled={isDecodedVinInvalid || isSubmitting}
              data-test="appraise-btn-by-vin"
              isActiveTab={isActiveTab}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

VinTabUI.propTypes = {
  vin: PropTypes.string,
  queryVin: PropTypes.string,
  isVinEdited: PropTypes.bool,
  setIsVinEdited: PropTypes.func,
  setStylesAndVin: PropTypes.func,
  ctaText: PropTypes.string,
  profileOfferVin: PropTypes.string,
  hasAddressToVinDrawer: PropTypes.bool,
  isLookUpStyleView: PropTypes.bool,
  isBackgroundStyleTabEnabled: PropTypes.bool,
  isVdpEmbedded: PropTypes.bool,
  isProjectEveStyle: PropTypes.bool,
  onFindVinModalOpen: PropTypes.func,
  onFindVinModalClose: PropTypes.func,
  vinDecoderStyles: PropTypes.shape({
    results: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isAppraiserPageOpened: PropTypes.bool,
  wasVinPrePopulated: PropTypes.bool,
  setWasVinPrePopulated: PropTypes.func,
  isActiveTab: PropTypes.bool,
  isPrivatePartyBtn: PropTypes.bool,
  isWIDG1125chal1: PropTypes.bool,
  isSellInCityState: PropTypes.bool,
  creativeId: PropTypes.string,
  storage: PropTypes.shape({
    get: PropTypes.func,
    set: PropTypes.func,
  }),
  apiMetrics: ApiMetrics,
  setModelValue: PropTypes.func,
  vinStyles: PropTypes.shape({
    matchType: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  view: appraisalTabViewPropType,
  isFullWidth: PropTypes.bool,
};

export const stateToPropsConfig = {
  vinStyles: bindToPath(({ vin }) => (vin ? buildStylesBasicPathFromVin({ vin }) : ''), VehicleVinModel),
};

const VinTabConnected = connectToModel(withMetrics(VinTabUI), stateToPropsConfig);

export function VinTab({ storage = null, ...restProps }) {
  const {
    onFindVinModalOpen,
    onFindVinModalClose,
    isProjectEveStyle,
    isLookUpStyleView,
    isBackgroundStyleTabEnabled,
    isWIDG1125chal1,
    isSellInCityState,
    isVdpEmbedded,
    creativeId,
    isFullWidth,
  } = useAppraisalTabsConfig();

  const {
    vin,
    isVinEdited,
    setIsVinEdited,
    queryVin,
    vinDecoderStyles,
    isAppraiserPageOpened,
    wasVinPrePopulated,
    setWasVinPrePopulated,
  } = useAppraisalTabsState();

  return (
    <VinTabConnected
      {...restProps}
      storage={storage}
      vin={vin}
      isVinEdited={isVinEdited}
      setIsVinEdited={setIsVinEdited}
      queryVin={queryVin}
      onFindVinModalOpen={onFindVinModalOpen}
      onFindVinModalClose={onFindVinModalClose}
      isAppraiserPageOpened={isAppraiserPageOpened}
      isProjectEveStyle={isProjectEveStyle}
      wasVinPrePopulated={wasVinPrePopulated}
      setWasVinPrePopulated={setWasVinPrePopulated}
      vinDecoderStyles={vinDecoderStyles}
      isLookUpStyleView={isLookUpStyleView}
      isBackgroundStyleTabEnabled={isBackgroundStyleTabEnabled}
      isWIDG1125chal1={isWIDG1125chal1}
      isSellInCityState={isSellInCityState}
      isVdpEmbedded={isVdpEmbedded}
      creativeId={creativeId}
      isFullWidth={isFullWidth}
    />
  );
}

VinTab.propTypes = {
  storage: PropTypes.shape({
    get: PropTypes.func,
    set: PropTypes.func,
  }),
};
